/**
 * UpcomingEvents (Dashboard widget)
 *
 * Calls GET /v1/organization/events (returns all events), then filters +
 * sorts client-side:
 *
 *   - Drop events whose event_date is before today.
 *   - Sort ascending by event_date (earliest upcoming first).
 *   - Take the top 7 for display.
 *
 * The backend doesn't yet expose a dedicated "upcoming events" endpoint
 * with server-side filtering. When it does, swap this client-side
 * filter for the server query.
 *
 * EventResponse shape reminder: { id, title, event_date, end_date,
 * event_type, description, all_day, created_at, created_by }.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  AccessTimeOutlined,
  EventOutlined,
  LocationOnOutlined,
  PersonOutlined,
} from '@mui/icons-material';

import { ApiError, organizationApi, type Schema } from '../../../../api';
import { WidgetErrorState } from '../../../../components/primitives';
import { useTranslation } from 'react-i18next';
import { formatDashboardDate, type WidgetTranslator } from './widgetDisplay';
import { translateApiError } from '../../../../i18n/translateApiError';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';

type Event = Schema<'EventResponse'>;

/** Days between today and the event. Negative = past, 0 = today. */
function daysUntil(eventDate: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ev = new Date(eventDate + 'T00:00:00');
  const ms = ev.getTime() - today.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function formatEventDate(e: Event, t: WidgetTranslator, locale: string): string {
  if (!e.event_date) return '—';
  const date = new Date(e.event_date + 'T00:00:00');
  const diff = daysUntil(e.event_date);
  if (diff === 0) return t('dashboard:widgets.upcomingEvents.today');
  if (diff === 1) return t('dashboard:widgets.upcomingEvents.tomorrow');
  return formatDashboardDate(date, locale, { month: 'short', day: 'numeric' });
}

function eventTypeLabel(t: WidgetTranslator, type: string | null | undefined): string {
  const known = new Set(['meeting', 'training', 'social', 'celebration', 'holiday', 'announcement']);
  const key = type && known.has(type) ? type : 'other';
  return t(`dashboard:widgets.upcomingEvents.eventTypes.${key}`);
}

function eventTypeColor(
  type: string | null | undefined,
  theme: Theme,
): { bgcolor: string; color: string } {
  switch (type) {
    case 'meeting':
      return {
        bgcolor: theme.palette.info.light,
        color: theme.palette.info.contrastText,
      };
    case 'training':
      return {
        bgcolor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
      };
    case 'social':
    case 'celebration':
      return {
        bgcolor: theme.palette.secondary.light,
        color: theme.palette.secondary.contrastText,
      };
    case 'holiday':
      return {
        bgcolor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
      };
    case 'announcement':
      return {
        bgcolor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
      };
    default:
      return {
        bgcolor: theme.palette.grey[300],
        color: theme.palette.text.primary,
      };
  }
}

function eventTypeIcon(type: string | null | undefined) {
  switch (type) {
    case 'meeting':
      return <PersonOutlined />;
    case 'training':
    case 'social':
    case 'celebration':
    case 'holiday':
    case 'announcement':
      return <EventOutlined />;
    default:
      return <EventOutlined />;
  }
}

const UpcomingEvents: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [rows, setRows] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationApi.eventsApi.list();
      setRows(data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.upcomingEvents.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents, t]);

  const upcoming = useMemo(() => {
    const enriched = rows
      .filter((e) => !!e.event_date && daysUntil(e.event_date) >= 0)
      .sort((a, b) => (a.event_date! > b.event_date! ? 1 : -1))
      .slice(0, 7);
    return enriched;
  }, [rows]);

  // Render -----------------------------------------------------------

  if (loading && rows.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error && rows.length === 0) {
    return <WidgetErrorState error={error} onRetry={fetchEvents} />;
  }

  if (upcoming.length === 0) {
    return (
      <WidgetEmptyState
        icon={EventOutlined}
        title={t('dashboard:widgets.upcomingEvents.emptyTitle')}
        hint={t('dashboard:widgets.upcomingEvents.emptyHint')}
      />
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header — no px of its own. The widget body container in
          Dashboard.tsx already applies p: 2, matching the shared title bar's
          px: 2, so an extra px: 1.5 here indented this card's summary line
          12px further than its own title and than every other widget's
          content. py stays: the vertical gap is this row's business. */}
      <Box sx={{ pt: 0, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              label={t('dashboard:widgets.upcomingEvents.eventCount', { count: upcoming.length })}
              color="primary"
              variant="outlined"
              sx={{
                height: 24,
                '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {t('dashboard:widgets.upcomingEvents.upcoming')}
            </Typography>
          </Box>
          <EventOutlined
            sx={{ color: theme.palette.primary.main, fontSize: 18 }}
          />
        </Box>
      </Box>

      {/* List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ py: 0, mt: -2 }}>
          {upcoming.map((event, index) => {
            const colors = eventTypeColor(event.event_type, theme);
            const isToday = daysUntil(event.event_date!) === 0;
            return (
              <ListItem
                key={event.id}
                component={Paper}
                elevation={0}
                onClick={() => navigate('/organization/events')}
                sx={{
                  py: 1,
                  mb: index === upcoming.length - 1 ? 0 : 0.5,
                  mt: index === 0 ? 3 : 0,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  border: '1px solid transparent',
                  transition: 'border-color 120ms',
                  '&:hover': { borderColor: 'divider' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: colors.bgcolor,
                      color: colors.color,
                    }}
                  >
                    {eventTypeIcon(event.event_type)}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main',
                          flex: 1,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {event.title}
                      </Typography>
                      {isToday && (
                        <Chip
                          size="small"
                          label={t('dashboard:widgets.upcomingEvents.todayBadge')}
                          color="error"
                          variant="filled"
                          sx={{
                            height: 20,
                            '& .MuiChip-label': {
                              px: 1,
                              fontSize: '0.70rem',
                              fontWeight: 600,
                            },
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <AccessTimeOutlined
                          sx={{ fontSize: 14, color: 'text.secondary' }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {formatEventDate(event, t, i18n.language)}
                          {event.all_day !== false
                            ? t('dashboard:widgets.upcomingEvents.allDaySuffix')
                            : ''}
                          {event.end_date && event.end_date !== event.event_date
                            ? t('dashboard:widgets.upcomingEvents.endDateSuffix', {
                              date: formatDashboardDate(
                                new Date(event.end_date + 'T00:00:00'),
                                i18n.language,
                                { month: 'short', day: 'numeric' },
                              ),
                            })
                            : ''}
                        </Typography>
                      </Box>
                      {event.event_type && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <LocationOnOutlined
                            sx={{ fontSize: 14, color: 'text.secondary' }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.75rem',
                            }}
                          >
                            {eventTypeLabel(t, event.event_type)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default UpcomingEvents;
